import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, MessageSquare, User } from 'lucide-react';
import { SearchResult } from '../types';
import { format } from 'date-fns';
import './CommandPalette.css';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => SearchResult[];
  onSelectResult: (result: SearchResult) => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onSearch,
  onSelectResult,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim()) {
      const searchResults = onSearch(query);
      setResults(searchResults.slice(0, 50)); // Limit to 50 results
      setSelectedIndex(0);
    } else {
      setResults([]);
    }
  }, [query, onSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev === 0 ? results.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      onSelectResult(results[selectedIndex]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="command-palette-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="command-palette"
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.2 }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <div className="command-palette-header">
            <Search size={20} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search messages across all chats..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          <div className="command-palette-results">
            {results.length === 0 && query && (
              <div className="no-results">
                <Search size={32} className="no-results-icon" />
                <p>No messages found</p>
              </div>
            )}

            {results.length === 0 && !query && (
              <div className="no-results">
                <Search size={32} className="no-results-icon" />
                <p>Type to search across all chats</p>
                <div className="search-tips">
                  <p className="tip">💡 Tips:</p>
                  <ul>
                    <li>Search by message text</li>
                    <li>Search by sender name</li>
                    <li>Use ↑↓ to navigate, Enter to select</li>
                  </ul>
                </div>
              </div>
            )}

            {results.map((result, index) => (
              <motion.div
                key={`${result.chatId}-${result.messageId}`}
                className={`search-result-item ${
                  index === selectedIndex ? 'selected' : ''
                }`}
                onClick={() => {
                  onSelectResult(result);
                  onClose();
                }}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
              >
                <div className="result-header">
                  <MessageSquare size={16} />
                  <span className="result-chat">{result.chatTitle}</span>
                  {result.message.sender && (
                    <>
                      <User size={14} className="sender-icon" />
                      <span className="result-sender">{result.message.sender}</span>
                    </>
                  )}
                  {result.message.unixMs > 0 && (
                    <span className="result-date">
                      {format(new Date(result.message.unixMs), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
                <div className="result-snippet">{result.snippet}</div>
              </motion.div>
            ))}
          </div>

          {results.length > 0 && (
            <div className="command-palette-footer">
              <span>
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
              <span className="keyboard-hint">
                ↑↓ Navigate • Enter Select • Esc Close
              </span>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
